import React, { useState, useEffect, useCallback } from 'react';
import { generateBlogPost, saveBlogPost, getAllBlogPosts, getBlogPostById, updateBlogPost, deleteBlogPost } from '../services/BlogService';
import { useAuth } from '../context/AuthContext';

function AdminBlog() {
    const [topic, setTopic] = useState('');
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [excerpt, setExcerpt] = useState('');
    const [tags, setTags] = useState([]);
    const [currentTag, setCurrentTag] = useState('');
    const [blogPosts, setBlogPosts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [editingId, setEditingId] = useState(null);
    const [isPublished, setIsPublished] = useState(false);
    const { currentUser } = useAuth();

    const fetchPosts = useCallback(async (page = 1) => {
        setLoading(true);
        setError(null);
        try {
            const data = await getAllBlogPosts(page);
            setBlogPosts(data.posts || []);
            setTotalPages(data.totalPages || 1);
            setCurrentPage(data.currentPage || 1);
        } catch (err) {
            console.error("Error fetching blog posts:", err);
            
            // Provide mock data
            const mockPosts = [
                {
                    _id: 'mock-1',
                    title: 'Top Recruiting Strategies for 2024',
                    author: 'Admin',
                    isPublished: true,
                    publishedAt: new Date().toISOString(),
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                    slug: 'top-recruiting-strategies-2024',
                    excerpt: 'Discover the most effective recruiting strategies for finding top talent in 2024.',
                    tags: ['recruiting', 'hiring', 'strategies']
                },
                {
                    _id: 'mock-2',
                    title: 'How to Prepare for a Technical Interview',
                    author: 'Admin',
                    isPublished: false,
                    publishedAt: null,
                    createdAt: new Date(Date.now() - 86400000).toISOString(), // 1 day ago
                    updatedAt: new Date(Date.now() - 86400000).toISOString(),
                    slug: 'prepare-technical-interview',
                    excerpt: 'Learn how to ace your next technical interview with these proven preparation tips.',
                    tags: ['interview', 'technical', 'preparation']
                }
            ];
            
            setBlogPosts(mockPosts);
            setTotalPages(1);
            setCurrentPage(1);
            setError(`The blog API returned an error: ${err.message}. Using mock data instead.`);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPosts();
    }, [fetchPosts]);

    const handleGenerate = async () => {
        if (!topic) {
            alert('Please enter a topic.');
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const generatedPost = await generateBlogPost(topic);
            setTitle(generatedPost.title || '');
            setContent(generatedPost.content || '');
            setExcerpt(generatedPost.excerpt || '');
            setTags(generatedPost.tags || []);
            setIsPublished(false); // Default to unpublished for generated posts
        } catch (err) {
            console.error("Error generating blog post:", err);
            
            // Provide fallback content
            setTitle(`Blog Post About: ${topic}`);
            setContent(`<h2>Introduction</h2>
<p>This is a placeholder content for a blog post about "${topic}". The blog generation API is currently unavailable.</p>
<h2>Key Points</h2>
<ul>
<li>Point 1 about ${topic}</li>
<li>Point 2 about ${topic}</li>
<li>Point 3 about ${topic}</li>
</ul>
<h2>Conclusion</h2>
<p>This is a conclusion paragraph about ${topic}.</p>`);
            setExcerpt(`A brief overview about ${topic} for job seekers and employers. Learn more about this important topic in the staffing industry.`);
            setTags(['placeholder', topic.toLowerCase().replace(/[^\w\s]/gi, '').split(' ')[0]]);
            
            setError(`The blog generation API returned an error: ${err.message}. Using placeholder content instead.`);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!title || !content) {
            alert('Please enter a title and content.');
            return;
        }

        setLoading(true);
        setError(null);

        const postData = {
            title,
            content,
            author: currentUser?.email || 'Admin',
            isPublished,
            excerpt,
            tags
        };

        try {
            if (editingId) {
                await updateBlogPost(editingId, postData);
            } else {
                await saveBlogPost(postData);
            }
            resetForm();
            fetchPosts();
            alert('Blog post saved successfully!');
        } catch (err) {
            console.error("Error saving blog post:", err);
            setError(`Error saving blog post: ${err.message}. The changes were not saved to the server, but you can still continue editing.`);
            
            // If we're in mock mode, simulate a successful save
            if (blogPosts.some(post => post._id.startsWith('mock-'))) {
                const mockId = `mock-${Date.now()}`;
                const mockPost = {
                    ...postData,
                    _id: editingId || mockId,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                    slug: title.toLowerCase().replace(/[^\w\s]/gi, '').replace(/\s+/g, '-')
                };
                
                if (!editingId) {
                    setBlogPosts(prev => [mockPost, ...prev]);
                } else {
                    setBlogPosts(prev => prev.map(p => p._id === editingId ? mockPost : p));
                }
                
                resetForm();
                alert('Blog post saved locally (mock mode).');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = async (id) => {
        setLoading(true);
        setError(null);
        try {
            // If it's a mock post, handle it locally
            if (id.startsWith('mock-')) {
                const mockPost = blogPosts.find(post => post._id === id);
                if (mockPost) {
                    setTitle(mockPost.title || '');
                    setContent(mockPost.content || '');
                    setExcerpt(mockPost.excerpt || '');
                    setTags(mockPost.tags || []);
                    setIsPublished(mockPost.isPublished || false);
                    setEditingId(id);
                    setTopic(''); // Clear topic when editing
                }
            } else {
                const post = await getBlogPostById(id);
                setTitle(post.title || '');
                setContent(post.content || '');
                setExcerpt(post.excerpt || '');
                setTags(post.tags || []);
                setIsPublished(post.isPublished || false);
                setEditingId(id);
                setTopic(''); // Clear topic when editing
            }
        } catch (err) {
            console.error("Error fetching blog post for editing:", err);
            setError(`Error fetching blog post: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handlePublishToggle = async (id, currentStatus) => {
        setLoading(true);
        setError(null);
        try {
            // If it's a mock post, handle it locally
            if (id.startsWith('mock-')) {
                setBlogPosts(prev => prev.map(post => {
                    if (post._id === id) {
                        return {
                            ...post,
                            isPublished: !currentStatus,
                            publishedAt: !currentStatus ? new Date().toISOString() : null
                        };
                    }
                    return post;
                }));
            } else {
                await updateBlogPost(id, { isPublished: !currentStatus });
                fetchPosts();
            }
        } catch (err) {
            console.error("Error updating publish status:", err);
            setError(`Error updating publish status: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this blog post?')) {
            setLoading(true);
            setError(null);
            try {
                // If it's a mock post, handle it locally
                if (id.startsWith('mock-')) {
                    setBlogPosts(prev => prev.filter(post => post._id !== id));
                } else {
                    await deleteBlogPost(id);
                    fetchPosts();
                }
            } catch (err) {
                console.error("Error deleting blog post:", err);
                setError(`Error deleting blog post: ${err.message}`);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleAddTag = () => {
        if (currentTag.trim() && !tags.includes(currentTag.trim())) {
            setTags([...tags, currentTag.trim()]);
            setCurrentTag('');
        }
    };

    const handleRemoveTag = (tagToRemove) => {
        setTags(tags.filter(tag => tag !== tagToRemove));
    };

    const resetForm = () => {
        setTitle('');
        setContent('');
        setExcerpt('');
        setTags([]);
        setCurrentTag('');
        setEditingId(null);
        setIsPublished(false);
        setTopic('');
    };

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= totalPages) {
            fetchPosts(newPage);
        }
    };

    return (
        <div className="container mx-auto p-4 bg-green-50 min-h-screen">
            <div className="my-6 bg-white p-6 rounded-lg shadow-md border-b-4 border-orange-500">
                <h2 className="text-2xl font-bold mb-4 text-green-800 pb-2 border-b border-green-100">Blog Post Generator</h2>
                <div className="mb-4">
                    <input
                        type="text"
                        value={topic}
                        onChange={(e) => setTopic(e.target.value)}
                        placeholder="Enter your recruiting or staffing topic..."
                        className="border p-2 rounded-md w-full focus:border-green-500 focus:ring-2 focus:ring-green-200"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                        Enter a recruiting or staffing related topic. For best results, target either job seekers or employers.
                    </p>
                    <div className="bg-green-50 p-4 rounded-md mt-3 border-l-4 border-green-500">
                        <h3 className="text-sm font-medium text-green-800 mb-2">Topic Examples for Employers:</h3>
                        <ul className="text-sm text-green-700 space-y-1 list-disc pl-5">
                            <li>"Top Strategies for Attracting IT Talent in Today's Market"</li>
                            <li>"Benefits of Using a Staffing Agency for Engineering Roles"</li>
                            <li>"How to Build an Effective Employee Retention Strategy"</li>
                            <li>"Streamlining Your Hiring Process with a Recruiting Partner"</li>
                        </ul>
                        <h3 className="text-sm font-medium text-green-800 mt-4 mb-2">Topic Examples for Job Seekers:</h3>
                        <ul className="text-sm text-green-700 space-y-1 list-disc pl-5">
                            <li>"How to Stand Out in Your IT Job Search"</li>
                            <li>"Engineering Career Paths: Finding Your Next Opportunity"</li>
                            <li>"Tips for a Successful Administrative Role Interview"</li>
                            <li>"Working with a Recruiter: What Job Seekers Need to Know"</li>
                        </ul>
                    </div>
                </div>

                <div className="mb-4">
                    <button onClick={handleGenerate} disabled={loading} className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-md w-full transition duration-200">
                        {loading ? 'Generating...' : 'Generate Blog Post'}
                    </button>
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-medium text-green-800 mb-1">Title</label>
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="border p-2 rounded-md w-full focus:border-green-500 focus:ring-2 focus:ring-green-200"
                        placeholder="Blog post title"
                    />
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-medium text-green-800 mb-1">Content</label>
                    <textarea
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        rows="10"
                        className="border p-2 rounded-md w-full focus:border-green-500 focus:ring-2 focus:ring-green-200"
                        placeholder="Blog post content (HTML supported)"
                    />
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-medium text-green-800 mb-1">Excerpt</label>
                    <textarea
                        value={excerpt}
                        onChange={(e) => setExcerpt(e.target.value)}
                        rows="3"
                        className="border p-2 rounded-md w-full focus:border-green-500 focus:ring-2 focus:ring-green-200"
                        placeholder="A short excerpt for the blog listing (optional)"
                    />
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-medium text-green-800 mb-1">Tags</label>
                    <div className="flex">
                        <input
                            type="text"
                            value={currentTag}
                            onChange={(e) => setCurrentTag(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                            className="border p-2 rounded-l-md w-full focus:border-green-500 focus:ring-2 focus:ring-green-200"
                            placeholder="Add a tag"
                        />
                        <button onClick={handleAddTag} className="bg-green-600 text-white px-4 py-2 rounded-r-md hover:bg-green-700 transition duration-200">Add Tag</button>
                    </div>
                    <div className="mt-2">
                        {tags.map((tag) => (
                            <span key={tag} className="inline-block bg-green-100 rounded-full px-3 py-1 text-sm font-semibold text-green-700 mr-2 mb-2">
                                {tag} <button onClick={() => handleRemoveTag(tag)} className="ml-1 text-green-800 focus:outline-none hover:text-orange-600">×</button>
                            </span>
                        ))}
                    </div>
                </div>

                <div className="mb-4 flex items-center">
                    <input
                        id="isPublished"
                        type="checkbox"
                        checked={isPublished}
                        onChange={(e) => setIsPublished(e.target.checked)}
                        className="mr-2 h-5 w-5 text-orange-500 focus:ring-orange-400"
                    />
                    <label htmlFor="isPublished" className="text-gray-700 select-none">Publish Immediately</label>
                </div>

                <div className="mb-4 flex space-x-2">
                    <button onClick={handleSave} disabled={loading} className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-md flex-1 transition duration-200">
                        {editingId ? 'Update Post' : 'Save Post'}
                    </button>
                    {editingId && (
                        <button onClick={resetForm} className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md flex-1 transition duration-200">
                            Cancel Edit
                        </button>
                    )}
                </div>

                {error && (
                    <div className="bg-red-50 border-l-4 border-orange-500 p-4 mb-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-red-700">{error}</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="my-6 bg-white p-6 rounded-lg shadow-md border-b-4 border-orange-500">
                <h2 className="text-2xl font-bold mb-4 text-green-800 pb-2 border-b border-green-100">Manage Blog Posts</h2>
                {loading ? (
                    <div className="flex justify-center py-8">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
                    </div>
                ) : error && blogPosts.length === 0 ? (
                    <div className="bg-yellow-50 border-l-4 border-orange-500 p-4 mb-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-yellow-700">{error}</p>
                            </div>
                        </div>
                    </div>
                ) : blogPosts.length === 0 ? (
                    <p className="text-gray-600 text-center py-8">No blog posts found.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-green-100">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-green-800 uppercase tracking-wider">Title</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-green-800 uppercase tracking-wider">Author</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-green-800 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-green-800 uppercase tracking-wider">Published Date</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-green-800 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {blogPosts.map((post) => (
                                    <tr key={post._id} className="hover:bg-green-50">
                                        <td className="px-6 py-4 whitespace-nowrap">{post.title}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">{post.author}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${post.isPublished ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                                {post.isPublished ? 'Published' : 'Draft'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">{post.publishedAt ? new Date(post.publishedAt).toLocaleDateString() : 'N/A'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <button onClick={() => handleEdit(post._id)} className="text-green-600 hover:text-green-800 mr-2 transition duration-200">
                                                Edit
                                            </button>
                                            <button onClick={() => handlePublishToggle(post._id, post.isPublished)} className={`${post.isPublished ? 'text-orange-500 hover:text-orange-700' : 'text-green-600 hover:text-green-800'} mr-2 transition duration-200`}>
                                                {post.isPublished ? 'Unpublish' : 'Publish'}
                                            </button>
                                            <button onClick={() => handleDelete(post._id)} className="text-red-600 hover:text-red-800 transition duration-200">
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div className="flex justify-center pb-6">
                <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-4 py-2 mr-2 rounded-md ${currentPage === 1 ? 'bg-gray-300 cursor-not-allowed' : 'bg-green-600 text-white hover:bg-green-700'} transition duration-200`}
                >
                    Previous
                </button>
                <span className="px-4 py-2 bg-white rounded-md border border-gray-300 text-gray-700">
                    Page {currentPage} of {totalPages}
                </span>
                <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`px-4 py-2 ml-2 rounded-md ${currentPage === totalPages ? 'bg-gray-300 cursor-not-allowed' : 'bg-orange-500 text-white hover:bg-orange-600'} transition duration-200`}
                >
                    Next
                </button>
            </div>
        </div>
    );
}

export default AdminBlog;